import axios, { AxiosRequestConfig, AxiosResponse } from "axios"
import { CartContents } from "./contexts/CartContext";

//Generate a random UUID as an idempotency key for the payment request
// length of idempotency_key should be less than 45
export function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    // eslint-disable-next-line no-mixed-operators
    var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Map the Postgres unified-order shape returned by /api/v1/orders/public/:id
// into the legacy IOrder shape the existing invoice components consume.
// Mapping is lossy (no imageId on line item snapshots, status → state, etc.)
// but good enough for the receipt page.
const STATUS_TO_LEGACY_STATE: Record<string, string> = {
  pending: 'OPEN',
  paid: 'PAID',
  fulfilled: 'CLOSED',
  cancelled: 'CANCELLED',
  refunded: 'REFUNDED',
};

const dollarsStringToCents = (s: string | undefined | null): number => {
  if (s === undefined || s === null) return 0;
  const n = parseFloat(s);
  if (Number.isNaN(n)) return 0;
  return Math.round(n * 100);
};

function adaptOrderToLegacyShape(payload: any): any {
  if (!payload) return payload;
  // Endpoint flattens `order` fields plus `lines: [...]` via #[serde(flatten)],
  // so the top level has both. Be tolerant of either shape.
  const order = payload.order ?? payload;
  const lines: any[] = payload.lines ?? order.lines ?? [];

  const items = lines.map((l: any) => ({
    item: {
      id: l.item_id,
      name: l.snapshot_name,
      brand: l.snapshot_brand,
      price: dollarsStringToCents(l.unit_price),
      imageId: undefined,
      categoryId: '',
      supplierUrls: [],
      homeUrls: [],
      externalId: {},
      inventory: {},
    },
    count: l.qty,
    tenant: l.tenant,
  }));

  return {
    id: order.legacy_business_id || order.id,
    items,
    state: STATUS_TO_LEGACY_STATE[order.status] ?? order.status,
    discountAmount: dollarsStringToCents(order.discount_total),
    // Carry the server-side monetary totals straight through so the receipt
    // shows what was actually charged (incl. tax) instead of recomputing
    // from line items — the recompute breaks on migrated orders whose lines
    // may be absent/unpriced, showing $0 tax. Left undefined (not 0) when
    // the payload lacks them, so the components' recompute fallback still
    // applies to order shapes that carry no server totals.
    subtotal: order.subtotal != null ? dollarsStringToCents(order.subtotal) : undefined,
    tax: order.tax_total != null
      ? { value: dollarsStringToCents(order.tax_total), name: 'Tax' }
      : undefined,
    total: order.total != null ? dollarsStringToCents(order.total) : undefined,
    discountCode: undefined,
    paymentMethod: order.payment_method,
    deliveryAddress: order.delivery_address,
    cardholderAddress: order.customer_address,
    customerAddress: order.customer_address,
    created_at: order.created_at,
    updated_at: order.updated_at,
    notes: undefined,
  };
}

class API {
  static instance: API;
  static id: string;

  ACTIVE_STORE_KEY: string = "ACTIVE_STORE";
  store: string | undefined = process.env.REACT_APP_DB_NAME as string;
  baseUrl: string = (process.env.REACT_APP_API_HOST as string);
  extendedUrl: string = (process.env.REACT_APP_API_HOST as string) + "/" + this.store;

  private constructor() {
    const myStore = window.localStorage.getItem(this.ACTIVE_STORE_KEY);

    if (!myStore) {
      this.store = process.env.REACT_APP_DB_NAME as string;
    }
    else {
      this.store = myStore;
    }
    this.extendedUrl = (process.env.REACT_APP_API_HOST as string) + "/" + this.store;
    API.id = uuidv4();
  }

  public static getInstance() : API {
    if (!API.instance) {
      API.instance = new API();
    }
    return API.instance;
  }

  public getStore() {
    return this.store;
  }

  getAxiosConfig = (accessToken: any, upload?: boolean) : AxiosRequestConfig => {
    const headers: Record<string,string> = {};

    if (!accessToken) {
      return {};
    }

    if (accessToken) {
      headers['Authorization'] = 'Bearer ' + accessToken;
    }
    if (upload) {
      headers['Content-Type'] ='multipart/form-data';
    }

    return {headers};
  }

  getShortItemName = (name: string): string => {
    if (!name) {
      return 'Unnamed';
    }
    const tokens = name.split(">");
    return tokens[tokens.length - 1].trim();
  }

  adaptItem = (item: IItem): IItem => {
    // Force UPC value
    const upc = item.codes?.upc ? item.codes.upc : item.sku;

    // More adapting
    return {
      ...item,
      name: this.getShortItemName(item.name),
      codes: {
        upc: upc,
        sku: item.codes?.sku
      },
      hidden: item.flags?.hidden,
      discontinued: item.flags?.discontinued,
      special: item.flags?.special
    }
  }


  getItems = async (accessToken?: string): Promise<IItem[]> => {
    try {
      const items: AxiosResponse<IItem[]> = await axios.get(this.extendedUrl + "/api/v1/items", this.getAxiosConfig(accessToken));
      return items.data.map((item) => {
        return this.adaptItem(item);
      });
    } catch (error: any) {
      throw new Error(error.response.data);
    }
  }

  getItemsByCategory = async (categoryId: string, accessToken?: string): Promise<IItem[]> => {
    try {
      const items: AxiosResponse<IItem[]> = await axios.get(this.extendedUrl + "/api/v1/items/category/" + categoryId, this.getAxiosConfig(accessToken));
      return items.data.map((item) => {
        return this.adaptItem(item);
      });
    } catch (error: any) {
      throw new Error(error.response.data);
    }
  }

  searchItems = async (text: string, accessToken?: string): Promise<IItem[]> => {
    if (!text) {
      return [];
    }
    try {
      const items: AxiosResponse<IItem[]> = await axios.get(this.extendedUrl + "/api/v1/items/search/" + text, this.getAxiosConfig(accessToken));
      return items.data.map((item) => {
        return this.adaptItem(item);
      });
    } catch (error: any) {
      throw new Error(error.response.data);
    }
  }

  // Search across every tenant in parallel; stamp each result with the tenant
  // it came from so callers can route the card link into the right shop, plus
  // the tenant's Sirv root so cross-tenant images resolve to the right CDN path
  // (each tenant has a different general.root_category). A failure in one
  // tenant doesn't kill the others — its slice just comes back empty.
  searchItemsAcrossTenants = async (text: string, tenants: string[], accessToken?: string): Promise<IItem[]> => {
    if (!text) {
      return [];
    }
    const perTenant = await Promise.all(
      tenants.map(async (tenant): Promise<IItem[]> => {
        try {
          const searchUrl = `${this.baseUrl}/${tenant}/api/v1/items/search/${text}`;
          const configUrl = `${this.baseUrl}/${tenant}/api/v1/config/client`;
          const [searchResp, configResp] = await Promise.all([
            axios.get<IItem[]>(searchUrl, this.getAxiosConfig(accessToken)),
            axios.get<any>(configUrl, this.getAxiosConfig(accessToken)).catch(() => undefined),
          ]);
          const imageRoot: string | undefined = configResp?.data?.general?.root_category;
          return searchResp.data.map((item) => ({
            ...this.adaptItem(item),
            tenant,
            imageRoot,
          }));
        } catch (error: any) {
          console.error(`Search failed for tenant ${tenant}:`, error?.message ?? error);
          return [];
        }
      })
    );
    return perTenant.flat();
  }

  // Most-recently-created items for one tenant. Used by the Home page
  // "New arrivals" card today (limit=1) and by the future list page
  // (larger limit). Co-fetches the tenant's config so the caller can
  // build a Sirv image URL — each tenant has a different
  // general.root_category, and the Home page renders cards for tenants
  // other than the currently-active one.
  getRecentItems = async (
    tenant: string,
    limit = 1,
    accessToken?: string,
  ): Promise<{ items: IItem[]; imageRoot: string | undefined }> => {
    return this.getRankedItems(tenant, 'recent', limit, accessToken);
  }

  // Best-selling items by total sales volume (online + reconciled
  // Square POS) over the server-configured window. Window length is
  // controlled by [best_sellers].window_days in config.toml; the
  // client just asks for the preset by name.
  getBestSellers = async (
    tenant: string,
    limit = 1,
    accessToken?: string,
  ): Promise<{ items: IItem[]; imageRoot: string | undefined }> => {
    return this.getRankedItems(tenant, 'best-sellers', limit, accessToken);
  }

  // Same shape as best sellers but over a shorter, recency-skewed
  // window — controlled by [hot_sellers].window_days. Shares the
  // server-side cache with best sellers via window-keyed entries.
  getHotSellers = async (
    tenant: string,
    limit = 1,
    accessToken?: string,
  ): Promise<{ items: IItem[]; imageRoot: string | undefined }> => {
    return this.getRankedItems(tenant, 'hot-sellers', limit, accessToken);
  }

  // Items the admin flipped "Featured" on, most-recent-first. The
  // server stamps `featured_at = now()` on toggle-on and clears it
  // on toggle-off, so the list is naturally curated.
  getFeaturedItems = async (
    tenant: string,
    limit = 1,
    accessToken?: string,
  ): Promise<{ items: IItem[]; imageRoot: string | undefined }> => {
    return this.getRankedItems(tenant, 'featured', limit, accessToken);
  }

  // Global news feed. No tenant prefix — the same posts render on
  // both shops' /news pages today. Returned newest-first by the
  // server (ORDER BY posted_on DESC, id DESC) so the page can render
  // the array as-is.
  getNews = async (
    limit = 200,
    offset = 0,
    accessToken?: string,
  ): Promise<INewsResponse> => {
    const url = `${this.baseUrl}/api/v1/news?limit=${limit}&offset=${offset}`;
    const response: AxiosResponse<INewsResponse> = await axios.get(
      url,
      this.getAxiosConfig(accessToken),
    );
    return response.data;
  }

  // Best Sellers / Hot Sellers landing pages: one top seller per leaf
  // category (a category with no children). No client limit — the
  // server returns at most one item per leaf, items already ordered
  // sold-DESC. Window is server-side per preset.
  getBestSellersByLeaf = async (
    tenant: string,
    accessToken?: string,
  ): Promise<{ items: IItem[]; imageRoot: string | undefined }> => {
    return this.getByLeaf(tenant, 'best-sellers', accessToken);
  }

  getHotSellersByLeaf = async (
    tenant: string,
    accessToken?: string,
  ): Promise<{ items: IItem[]; imageRoot: string | undefined }> => {
    return this.getByLeaf(tenant, 'hot-sellers', accessToken);
  }

  private getByLeaf = async (
    tenant: string,
    preset: 'best-sellers' | 'hot-sellers',
    accessToken?: string,
  ): Promise<{ items: IItem[]; imageRoot: string | undefined }> => {
    try {
      const itemsUrl = `${this.baseUrl}/${tenant}/api/v1/items/${preset}/by-leaf`;
      const configUrl = `${this.baseUrl}/${tenant}/api/v1/config/client`;
      const [itemsResp, configResp] = await Promise.all([
        axios.get<IItem[]>(itemsUrl, this.getAxiosConfig(accessToken)),
        axios.get<any>(configUrl, this.getAxiosConfig(accessToken)).catch(() => undefined),
      ]);
      const imageRoot: string | undefined = configResp?.data?.general?.root_category;
      return {
        items: itemsResp.data.map((item) => this.adaptItem(item)),
        imageRoot,
      };
    } catch (error: any) {
      console.error(`By-leaf (${preset}) failed for tenant ${tenant}:`, error?.message ?? error);
      return { items: [], imageRoot: undefined };
    }
  }

  // Shared helper for any per-tenant ranked-items endpoint that
  // returns the same `IItem[]` shape (recent, best-sellers,
  // hot-sellers). Co-fetches client config so callers rendering
  // cross-tenant cards can build a Sirv image URL.
  private getRankedItems = async (
    tenant: string,
    endpoint: 'recent' | 'best-sellers' | 'hot-sellers' | 'featured',
    limit: number,
    accessToken?: string,
  ): Promise<{ items: IItem[]; imageRoot: string | undefined }> => {
    try {
      const itemsUrl = `${this.baseUrl}/${tenant}/api/v1/items/${endpoint}?limit=${limit}`;
      const configUrl = `${this.baseUrl}/${tenant}/api/v1/config/client`;
      const [itemsResp, configResp] = await Promise.all([
        axios.get<IItem[]>(itemsUrl, this.getAxiosConfig(accessToken)),
        axios.get<any>(configUrl, this.getAxiosConfig(accessToken)).catch(() => undefined),
      ]);
      const imageRoot: string | undefined = configResp?.data?.general?.root_category;
      return {
        items: itemsResp.data.map((item) => this.adaptItem(item)),
        imageRoot,
      };
    } catch (error: any) {
      console.error(`Ranked items (${endpoint}) failed for tenant ${tenant}:`, error?.message ?? error);
      return { items: [], imageRoot: undefined };
    }
  }

  searchItemsByNameAndPrice = async (text: string, price: number, accessToken?: string): Promise<IItem[]> => {
    try {
      const items: AxiosResponse<IItem[]> = await axios.get(`${this.extendedUrl}/api/v1/items/search/${text}/price/${price}`, this.getAxiosConfig(accessToken));
      return items.data.map((item) => {
        return this.adaptItem(item);
      });
    } catch (error: any) {
      throw new Error(error.response.data);
    }
  }

  getItemById = async (itemId: string, accessToken?: string, store?: string): Promise<IItem> => {
    // `store` lets callers (e.g. mixed-tenant cart validation) hit a tenant
    // other than the currently-active one; defaults to the active tenant.
    const url = store
      ? `${this.baseUrl}/${store}/api/v1/items/${itemId}`
      : `${this.extendedUrl}/api/v1/items/${itemId}`;
    try {
      const item: AxiosResponse<IItem> = await axios.get(url, this.getAxiosConfig(accessToken));
      return this.adaptItem(item.data);
    } catch (error: any) {
      // Network failures (no `response`) used to crash here on `error.response.data`.
      const msg = error?.response?.data ?? error?.message ?? String(error);
      throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  }

  getAiPersonalities = async (accessToken?: string): Promise<Array<{ id: string; displayName?: string; disclaimer: string | null }>> => {
    try {
      const response: AxiosResponse<any> = await axios.get(
        this.extendedUrl + "/api/v1/scrape/ai/personalities",
        this.getAxiosConfig(accessToken));
      const data = response.data;
      if (data && Array.isArray(data.personalities)) {
        return data.personalities;
      }
      if (Array.isArray(data)) {
        return data;
      }
      return [];
    } catch (error: any) {
      return [];
    }
  }

  getItemDescriptions = async (itemId: string, accessToken?: string, store?: string): Promise<Record<string, string>> => {
    // Optional `store` for the same reason as `getItemById`: the dialog
    // can open over a page where the API singleton's active store
    // doesn't match the item's tenant.
    const base = store
      ? `${this.baseUrl}/${store}`
      : this.extendedUrl;
    try {
      const response: AxiosResponse<Record<string, string>> = await axios.get(
        base + "/api/v1/items/" + itemId + "/descriptions",
        this.getAxiosConfig(accessToken));
      return response.data && typeof response.data === 'object' ? response.data : {};
    } catch (error: any) {
      // Missing descriptions endpoint or no descriptions yet is non-fatal; return empty map.
      return {};
    }
  }

  getVariantsById = async (itemId: string, accessToken?: string, store?: string): Promise<IItem[]> => {
    const base = store
      ? `${this.baseUrl}/${store}`
      : this.extendedUrl;
    try {
      const items: AxiosResponse<IItem[]> = await axios.get(base + "/api/v1/items/" + itemId + "/variants", this.getAxiosConfig(accessToken));
      return items.data.map((item) => {
        return this.adaptItem(item);
      });
    } catch (error: any) {
      throw new Error(error.response.data);
    }
  }

  getImagesById = async (imageId: string): Promise<IImage[]> => {
    try {
      const items: AxiosResponse<IImage[]> = await axios.get(this.extendedUrl + "/api/v1/images/" + imageId);
      return items.data;
    } catch (error: any) {
      throw new Error(error.response.data);
    }
  }

  findCategoryByName = (cats: ICategory[], name: string): ICategory | undefined => {
    for (let i = 0; i < cats.length; i++) {
      const cat = cats[i];
      if (cat.name === name) {
        return cat;
      }
    }
    return undefined;
  }

  categorySort = (cat1: ICategory, cat2: ICategory): number => {
    return cat1.shortName < cat2.shortName ? -1 : cat1.shortName > cat2.shortName ? 1 : 0;
  }

  getShortCategoryName = (name: string) => {
    return name;
  }

  getCategoryById = async (id: string): Promise<ICategory | undefined> => {
    try {
      const categories: AxiosResponse<ICategory[]> = await axios.get(this.extendedUrl + "/api/v1/categories");

      for (let i = 0; i < categories.data.length; i++) {
        const category = categories.data[i];
        if (category.id === id) {
          return category;
        }
      }

      return undefined;
    } catch (error: any) {
      throw new Error(error.response.data);
    }
  }

  getCategoryPathById = async (id: string): Promise<ICategory[]> => {
    try {
      const categories = await this.getCategories();

      for (let i = 0; i < categories.length; i++) {
        const path = this.doGetCategoryPath(id, categories[i]);
        if (path.length > 0) {
          return path;
        }
      }

      return [];
    } catch (error: any) {
      throw new Error(error.response.data);
    }
  }

  doGetCategoryPath = (id: string, category: ICategory): ICategory[] => {
    if (category.id === id) {
      return [category];
    }
    if (category.name === id) {
      return [category];
    }
    if (category.children) {
      for (let i = 0; i < category.children.length; i++) {
        const child = category.children[i];
        const path = this.doGetCategoryPath(id, child);
        if (path.length > 0) {
          return [category, ...path];
        }
      }
    }
    return [];
  }

  setActiveStore = async (name?: string) => {
    this.store = name ? name : "backroom";
    this.extendedUrl = (process.env.REACT_APP_API_HOST as string) + "/" + this.store;

    window.localStorage.setItem(this.ACTIVE_STORE_KEY, this.store);
  }

  /**
   * Get the category tree
   */
  getCategories = async (showHidden?: boolean): Promise<ICategory[]> => {
    try {
      const categories: AxiosResponse<ICategory[]> = await axios.get(this.extendedUrl + "/api/v1/categories");

      // Make a lookup table of id to category
      const lookup: Record<string,ICategory> = {};
      for (let i = 0; i < categories.data.length; i++) {
        const category = categories.data[i];
        if (!category.children) {
          category.children = [];
        }
        lookup[category.id] = category;
      }

      // Connect children
      const roots: Record<string,ICategory> = {};
      for (let i = 0; i < categories.data.length; i++) {
        const category = categories.data[i];
        if (category.parentId !== "1") {
          const parent = lookup[category.parentId];
          if (!parent) {
            continue;
          }
          parent.children.push(category);
        }
        else {
          roots[category.id] = category;
        }
      }

      // Leaves don't have items and are marked specially
      for (let i = 0; i < categories.data.length; i++) {
        const category = categories.data[i];

        category.shortName = category.name;
        category.url = `/r/${this.store}/category/${category.id}`;

        // Ignore the hidden folder unless we are explicitly told to
        if (!showHidden && category.shortName === "Hidden") {
          continue;
        }
      }

      const results = Object.values(roots);
      results.sort(this.categorySort);
      for (let i = 0; i < results.length; i++) {
        const category = results[i];
        category.children.sort(this.categorySort);
      }

      return results;
    } catch (error: any) {
      throw new Error(error);
    }
  }

  /**
   * Get the configuration from the server
   */
  getServerConfig = async (): Promise<IConfig> => {
    try {
      const config: AxiosResponse<IConfig> = await axios.get(this.extendedUrl + "/api/v1/config");
      return config.data;
    } catch (error: any) {
      throw new Error(error.response.data);
    }
  }

  /**
   * Submit an order
   * @returns the order ID
   */
  // Unified storefront checkout — writes directly to Postgres via backroom-server
  // (Phase 2T). Bypasses the legacy per-tenant Mongo orders.
  submitOrder = async (cart: CartContents, idToken?: string | null): Promise<any> => {
    try {
      // Optional bearer — when present, backroom-server attaches customer_id
      // and may save default addresses. Guest checkout works fine without.
      const headers: Record<string, string> = {};
      if (idToken) headers.Authorization = `Bearer ${idToken}`;
      const result = await axios.post(`${this.baseUrl}/api/v1/checkout`, { cart }, { headers });
      if (result.status === 200 || result.status === 201) {
        return { id: result.data.id, token: result.data.token };
      }
    } catch (error: any) {
      const msg = error?.response?.data ?? error?.message ?? String(error);
      throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  }

  // Public order fetch (Postgres). The endpoint returns the unified
  // `{order, lines}` shape; we adapt to the legacy IOrder shape here so
  // the existing InvoiceReview components can render unchanged.
  getOrderById = async (id: string, accessToken?: string): Promise<any | undefined> => {
    try {
      const result = await axios.get(`${this.baseUrl}/api/v1/orders/public/${id}`, this.getAxiosConfig(accessToken));
      if (result.status === 200) {
        return adaptOrderToLegacyShape(result.data);
      }
    } catch (error: any) {
      const msg = error?.response?.data ?? error?.message ?? String(error);
      throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  }

  /**
   * Special state change which can be done temporally close to the creation
   */
  cancelOrderById = async(orderId: string, token: string): Promise<void> => {
    try {
      await axios.post(
        `${this.baseUrl}/api/v1/orders/${orderId}/cancel?token=${encodeURIComponent(token)}`
      );
    } catch (error: any) {
      const msg = error?.response?.data ?? error?.message ?? String(error);
      throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
    }
  }

  getDiscount = async (discountId: string): Promise<IDiscount|undefined> => {
    try {
      const result = await axios.get(`${this.extendedUrl}/api/v1/order/discount/${discountId}`);
      if (result.status === 200) {
        return result.data;
      }
    } catch (error: any) {
      throw new Error(error.response.data);
    }
  }

  getClientConfig = async (name: string, accessToken?: string, store?: string): Promise<Record<string, Object>> => {
    // Optional `store` for the same reason `getItemById` accepts it:
    // the item-details dialog can open over a page where the API
    // singleton's active store doesn't match the URL's tenant, and we
    // need the URL-tenant's config (specifically `general.root_category`)
    // to build the right Sirv image URL.
    const base = store
      ? `${this.baseUrl}/${store}`
      : this.extendedUrl;
    try {
      const roles: AxiosResponse<Record<string, Object>> = await axios.get(base + "/api/v1/config/" + name, this.getAxiosConfig(accessToken));
      const json = roles.data as any;
      return json;
    } catch (error: any) {
      throw new Error(error.response.data);
    }
  }

  /**
   * Call this function to send a payment token, buyer name, and other details
   * to the project server code so that a payment can be created with
   * Payments API
   *
   * https://developer.squareup.com/docs/web-payments/take-card-payment
   */
  createPayment = async (
    provider: string,
    locationId: string,
    environment: string,
    token: string,
    orderId: string,
    accessToken?: string): Promise<any> => {
    const body = {
      provider: provider,
      locationId: locationId,
      environment: environment,
      token: token,
      orderId: orderId,
      idempotency_key: uuidv4()
    };

    try {
      // Routed to backroom-server (no tenant prefix). Rust loads the
      // Postgres order, charges Paysafe, marks paid. Replaces tqos-server's
      // Mongo-backed payment endpoint.
      const response = await axios.post(`${this.baseUrl}/api/v1/payment`, body, this.getAxiosConfig(accessToken));
      return response.data;
    } catch (error: any) {
      throw error.response.data;
    }
  }
}

export default API.getInstance();
